/**
 * ob-agent transport — the website chatbot's backend client.
 *
 * Speaks the ob-agent public protocol (replaces the legacy PHP
 * /v1/external/chat-website polling contract):
 *   POST {agentApiUrl}/agent/public/website/chat            → SSE stream
 *   GET  {agentApiUrl}/agent/public/website/sessions/:id/messages  → history
 *
 * Session identity is a server-issued UUID kept in localStorage — no
 * fingerprinting, no cookies. The SSE stream carries the whole turn:
 * `session` (id to persist) → `delta`* (real streamed text) → `suggestions`
 * (quick replies) → `done` → stream end; `error` replaces `done` on failure.
 * Unknown event types are ignored on purpose: the protocol is additive (the
 * future human-reply channel arrives as a new role/event, not a breaking
 * change — see `history()` and the 'human_agent' role).
 */

import { serverTimestampToIso } from '@/lib/chat-transcript';
import { stripPageContextPrefix } from '@/lib/page-context';
import { IChatbotMessage } from '@/interfaces';
import { IAgentForm, IStreamChatHandlers } from '@/interfaces/obAgent';
import { getWebsiteSessionUuid } from '@/session-replay';

const AGENT_SESSION_UUID_KEY = 'chat_agent_session_uuid';
// v2: fresh key so transcripts cached by the legacy widget (which stored raw
// HTML from the old backend) are never fed into the new renderer.

// Get current local timestamp in "YYYY-MM-DD HH:mm:ss" format (display only).
export function getLocalTimestamp(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
    `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  );
}

export function getStoredAgentSessionUuid(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(AGENT_SESSION_UUID_KEY);
  } catch {
    return null;
  }
}

function storeAgentSessionUuid(agentSessionUuid: string): void {
  try {
    localStorage.setItem(AGENT_SESSION_UUID_KEY, agentSessionUuid);
  } catch {
    // Private mode etc. — the chat still works, it just won't survive reloads.
  }
}

async function agentApiUrl(): Promise<string> {
  const { environment } = await import('@/config/environment');
  return environment.api.agentApiUrl.replace(/\/$/, '');
}

/**
 * Send one user message and stream the assistant's answer.
 * Resolves when the stream ends (after onDone/onError has fired).
 */
export async function streamChat(
  message: string,
  handlers: IStreamChatHandlers,
  pagePath?: string,
  language?: 'en' | 'zh' | 'cn'
): Promise<void> {
  // Every website interaction carries the marketing page automatically.
  // Callers may pass an explicit path (ChatWidget does); forms/background
  // bridges safely fall back to the browser location instead of losing the
  // sales context.
  const resolvedPagePath =
    pagePath ??
    (typeof window !== 'undefined' ? window.location.pathname : undefined);
  let base: string;
  try {
    base = await agentApiUrl();
  } catch {
    handlers.onError('Chat is not configured.', false);
    return;
  }

  let response: Response;
  try {
    response = await fetch(`${base}/agent/public/website/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        agent_session_uuid: getStoredAgentSessionUuid() ?? undefined,
        website_session_uuid: getWebsiteSessionUuid() ?? undefined,
        ...(language ? { lang: language === 'zh' ? 'cn' : language } : {}),
        ...(resolvedPagePath ? { page: { path: resolvedPagePath } } : {}),
      }),
    });
  } catch {
    handlers.onError('offline', true);
    return;
  }

  if (!response.ok || !response.body) {
    // 429 = per-IP rate limit / daily budget; both are "try again shortly".
    handlers.onError(
      response.status === 429 ? 'busy' : `HTTP ${response.status}`,
      response.status === 429 || response.status === 503
    );
    return;
  }

  // Minimal SSE parse: events are blank-line-separated "event:"/"data:" blocks;
  // ": ping" heartbeat comments are skipped by the event/data filter.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let doneFired = false;

  const handleBlock = async (block: string): Promise<void> => {
    let eventType = '';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (!eventType || !data) return;
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    switch (eventType) {
      case 'session':
        if (typeof payload.agent_session_uuid === 'string')
          storeAgentSessionUuid(payload.agent_session_uuid);
        break;
      case 'delta':
        if (typeof payload.text === 'string') handlers.onDelta(payload.text);
        break;
      case 'suggestions':
        if (Array.isArray(payload.suggestions)) {
          handlers.onSuggestions?.(payload.suggestions.map(s => String(s)));
        }
        break;
      case 'done':
        doneFired = true;
        handlers.onDone(typeof payload.text === 'string' ? payload.text : '');
        break;
      case 'tool_result':
        if (
          typeof payload.tool === 'string' &&
          typeof payload.ok === 'boolean'
        ) {
          handlers.onToolResult?.({
            tool: payload.tool,
            ok: payload.ok,
            ...(typeof payload.delivered === 'boolean'
              ? { delivered: payload.delivered }
              : {}),
          });
        }
        break;
      case 'form': {
        const form = payload.form as Record<string, unknown> | undefined;
        if (
          typeof payload.requestId === 'string' &&
          form &&
          Array.isArray(form.questions)
        ) {
          await handlers.onForm?.({
            requestId: payload.requestId,
            title: typeof form.title === 'string' ? form.title : undefined,
            description:
              typeof form.description === 'string'
                ? form.description
                : undefined,
            questions: form.questions as IAgentForm['questions'],
          });
        }
        break;
      }
      case 'error':
        doneFired = true;
        handlers.onError(
          typeof payload.message === 'string' ? payload.message : 'error',
          false
        );
        break;
      // status / anything future (human replies!) — ignored here.
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        await handleBlock(buffer.slice(0, idx));
        buffer = buffer.slice(idx + 2);
      }
    }
  } catch {
    if (!doneFired) handlers.onError('connection lost', true);
    return;
  }
  if (!doneFired) handlers.onError('connection lost', true);
}

/**
 * Server display history for the stored session, mapped into the widget's
 * message shape. Includes any 'human_agent' rows a staff member may have
 * written (the future human-reply channel) — they render like assistant
 * messages with their own role. Returns [] when there is no session yet.
 */
export async function fetchHistory(): Promise<IChatbotMessage[]> {
  const agentSessionUuid = getStoredAgentSessionUuid();
  if (!agentSessionUuid) return [];
  try {
    const base = await agentApiUrl();
    const res = await fetch(
      `${base}/agent/public/website/sessions/${encodeURIComponent(agentSessionUuid)}/messages`
    );
    if (!res.ok) return [];
    const json = (await res.json()) as {
      messages?: {
        agent_message_id?: number | string;
        agent_message_role?: string;
        role?: string;
        content: string;
        suggestions?: string[];
        agent_message_insert_time?: string | null;
        created_at?: string | null;
      }[];
    };
    return (json.messages ?? [])
      .filter(m => m.content)
      .map(m => {
        const role = m.agent_message_role ?? m.role;
        // Defensive: the server strips the injected "[Context: user is on …]"
        // line, but a stale edge cache may still carry it. The widget matches
        // restored rows to its local copies by exact text, so the prefix must
        // never survive into the message list.
        const content =
          role === 'user' ? stripPageContextPrefix(m.content) : m.content;
        const id = Number(m.agent_message_id);
        return {
          // The server row id is the read watermark's unit; a row without one
          // (should not happen) is treated as already read.
          ai_chatbot_message_id: Number.isFinite(id) && id > 0 ? id : null,
          ai_chatbot_message_role: (role === 'user'
            ? 'user'
            : role === 'human_agent'
              ? 'human_agent'
              : 'assistant') as IChatbotMessage['ai_chatbot_message_role'],
          ai_chatbot_message_text: content,
          ai_chatbot_message_quick_replies_json: m.suggestions?.length
            ? JSON.stringify(m.suggestions)
            : null,
          ai_chatbot_message_read: 1 as const,
          ai_chatbot_message_type: 'website' as const,
          ai_chatbot_message_insert_time:
            serverTimestampToIso(m.agent_message_insert_time ?? m.created_at) ??
            getLocalTimestamp(),
        };
      });
  } catch {
    return [];
  }
}

/**
 * Answer (or cancel) a pending in-chat form. The generating run is parked on
 * the open SSE stream; a successful respond resumes it there.
 */
export async function respondToForm(
  requestId: string,
  data: {
    answers: Record<string, string | boolean | string[]>;
    cancelled?: boolean;
  }
): Promise<boolean> {
  const agentSessionUuid = getStoredAgentSessionUuid();
  if (!agentSessionUuid) return false;
  try {
    const base = await agentApiUrl();
    const res = await fetch(
      `${base}/agent/public/website/sessions/${encodeURIComponent(agentSessionUuid)}/respond`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: requestId,
          data: {
            ...data.answers,
            ...(data.cancelled ? { cancelled: true } : {}),
          },
        }),
      }
    );
    return res.ok;
  } catch {
    return false;
  }
}
